import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Shield, LogOut, Bell, BellOff, Mail, Phone, Calendar, TrendingUp, Briefcase } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [showIntakeForms, setShowIntakeForms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedIntakeForm, setSelectedIntakeForm] = useState(null);
  const [unviewedCount, setUnviewedCount] = useState(0);
  const [intakeUnviewedCount, setIntakeUnviewedCount] = useState(0);

  useEffect(() => {
    fetchLeads();
    fetchIntakeForms();
    fetchNotificationStatus();
    fetchIntakeNotifications();
    // Poll for new submissions every 30 seconds
    const interval = setInterval(() => {
      fetchNotificationStatus();
      fetchIntakeNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        onLogout();
      } else {
        toast.error("Failed to fetch leads");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationStatus = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnviewedCount(response.data.unviewed_count);
    } catch (error) {
      console.error("Error fetching notification status:", error);
    }
  };

  const fetchIntakeForms = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/intake-forms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIntakeForms(response.data);
    } catch (error) {
      console.error("Error fetching intake forms:", error);
    }
  };

  const fetchIntakeNotifications = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/intake-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIntakeUnviewedCount(response.data.unviewed_count);
    } catch (error) {
      console.error("Error fetching intake notifications:", error);
    }
  };

  const markAsViewed = async (leadId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/leads/${leadId}/mark-viewed`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Update local state
      setLeads(leads.map(item => 
        item.lead.id === leadId ? { ...item, lead: { ...item.lead, viewed: true } } : item
      ));
      setUnviewedCount(Math.max(0, unviewedCount - 1));
    } catch (error) {
      console.error("Error marking lead as viewed:", error);
    }
  };

  const handleRowClick = (leadData) => {
    setSelectedLead(leadData);
    if (!leadData.lead.viewed) {
      markAsViewed(leadData.lead.id);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #F5F5F5, #FFFFFF, #FAFAFA)' }}>
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="/financial-flight-logo.png" 
                alt="Financial Flight" 
                className="w-10 h-10"
              />
              <span className="text-xl font-bold" style={{ color: '#2E2E2E' }}>Financial Flight</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowIntakeForms(false)}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  !showIntakeForms ? 'text-white font-semibold' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={!showIntakeForms ? { backgroundColor: '#D4AF37', color: '#2E2E2E' } : {}}
              >
                Insurance Leads
              </button>
              <button
                onClick={() => setShowIntakeForms(true)}
                className={`px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${
                  showIntakeForms ? 'text-white font-semibold' : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={showIntakeForms ? { backgroundColor: '#D4AF37', color: '#2E2E2E' } : {}}
              >
                <Briefcase className="w-3.5 h-3.5" />
                Web Dev Inquiries
                {intakeUnviewedCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {intakeUnviewedCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              {unviewedCount > 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FFF9E6', color: '#2E2E2E' }} data-testid="notification-indicator">
                  <Bell className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  <span className="text-sm font-semibold">{unviewedCount} new</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-500 rounded-lg">
                  <BellOff className="w-4 h-4" />
                  <span className="text-sm">No new leads</span>
                </div>
              )}
            </div>
            <Button
              onClick={onLogout}
              variant="outline"
              className="flex items-center gap-2 border-gray-300"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Leads</p>
                <p className="text-3xl font-bold" style={{ color: '#2E2E2E' }} data-testid="total-leads-count">{leads.length}</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF9E6' }}>
                <TrendingUp className="w-6 h-6" style={{ color: '#D4AF37' }} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Unviewed Leads</p>
                <p className="text-3xl font-bold" style={{ color: '#D4AF37' }} data-testid="unviewed-leads-count">{unviewedCount}</p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF9E6' }}>
                <Bell className="w-6 h-6" style={{ color: '#D4AF37' }} />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Quotes Generated</p>
                <p className="text-3xl font-bold" style={{ color: '#2E2E2E' }} data-testid="quotes-count">
                  {leads.filter(l => l.quote).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Conditional Content - Insurance Leads or Intake Forms */}
        {!showIntakeForms ? (
          <>
        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold" style={{ color: '#2E2E2E' }}>All Leads</h2>
            <p className="text-sm text-gray-600 mt-1">Click on a row to view details</p>
          </div>

          {loading ? (
            <div className="p-12 text-center text-gray-500">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No leads yet. They will appear here once submitted.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Quote
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leads.map((item, index) => (
                    <tr
                      key={item.lead.id}
                      onClick={() => handleRowClick(item)}
                      className={`cursor-pointer transition-colors ${
                        !item.lead.viewed ? '' : ''
                      } ${selectedLead?.lead.id === item.lead.id ? '' : 'hover:bg-gray-50'}`}
                      style={!item.lead.viewed ? { backgroundColor: '#FFF9E6' } : selectedLead?.lead.id === item.lead.id ? { backgroundColor: '#FFF9E6' } : {}}
                      data-testid={`lead-row-${index}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!item.lead.viewed && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}>
                            New
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900" data-testid={`lead-name-${index}`}>
                          {item.lead.full_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            {item.lead.email}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            {item.lead.phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.quote ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Generated
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-3 h-3" />
                          {formatDate(item.lead.timestamp)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Lead Details Panel */}
        {selectedLead && (
          <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-lg p-6" data-testid="lead-details-panel">
            <h3 className="text-xl font-bold mb-6" style={{ color: '#2E2E2E' }}>Lead Details</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Contact Information</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Full Name</p>
                    <p className="text-sm font-medium text-gray-900" data-testid="detail-name">{selectedLead.lead.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="text-sm text-gray-900" data-testid="detail-email">{selectedLead.lead.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <p className="text-sm text-gray-900" data-testid="detail-phone">{selectedLead.lead.phone}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Submission Date</p>
                    <p className="text-sm text-gray-900">{formatDate(selectedLead.lead.timestamp)}</p>
                  </div>
                </div>
              </div>

              {/* Quote Information */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Quote Information</h4>
                {selectedLead.quote ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500 mb-1">Age</p>
                        <p className="font-medium text-gray-900" data-testid="detail-age">{selectedLead.quote.age}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Gender</p>
                        <p className="font-medium text-gray-900 capitalize" data-testid="detail-gender">{selectedLead.quote.gender}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">Smoking</p>
                        <p className="font-medium text-gray-900 capitalize" data-testid="detail-smoking">
                          {selectedLead.quote.smoking_status.replace('_', ' ')}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 mb-1">State</p>
                        <p className="font-medium text-gray-900" data-testid="detail-state">{selectedLead.quote.state || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-3">Monthly Premium Estimates</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">10-Year Term</p>
                          <p className="text-lg font-bold text-gray-900" data-testid="detail-term10">${selectedLead.quote.term_10}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">20-Year Term</p>
                          <p className="text-lg font-bold text-gray-900" data-testid="detail-term20">${selectedLead.quote.term_20}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">30-Year Term</p>
                          <p className="text-lg font-bold text-gray-900" data-testid="detail-term30">${selectedLead.quote.term_30}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Whole Life</p>
                          <p className="text-lg font-bold text-gray-900" data-testid="detail-whole">${selectedLead.quote.whole_life}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No quote generated yet for this lead.</p>
                )}
              </div>
            </div>
          </div>
        )}
          </>
        ) : (
          /* Intake Forms Section */
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" style={{ color: '#D4AF37' }} />
                <h2 className="text-xl font-bold" style={{ color: '#2E2E2E' }}>Web Development Inquiries</h2>
              </div>
              <p className="text-sm text-gray-600 mt-1">Project requests from insurance agents/agencies</p>
            </div>

            {intakeForms.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                No project inquiries yet. Share your intake form link to receive submissions.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Project Type</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Package</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {intakeForms.map((form, index) => (
                      <tr
                        key={form.id}
                        onClick={() => setSelectedIntakeForm(form)}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        style={!form.viewed ? { backgroundColor: '#FFF9E6' } : {}}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          {!form.viewed && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}>
                              New
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{form.company_name}</div>
                          <div className="text-xs text-gray-500">{form.industry}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{form.contact_name}</div>
                          <div className="text-xs text-gray-500">{form.email}</div>
                          <div className="text-xs text-gray-500">{form.phone}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{form.purpose}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{form.budget_range || 'Not specified'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{formatDate(form.timestamp)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Selected Intake Form Details */}
        {selectedIntakeForm && showIntakeForms && (
          <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-lg p-6">
            <h3 className="text-xl font-bold mb-6" style={{ color: '#2E2E2E' }}>Project Inquiry Details</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Company Information</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Company Name</p>
                    <p className="text-sm font-medium text-gray-900">{selectedIntakeForm.company_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Industry</p>
                    <p className="text-sm text-gray-900">{selectedIntakeForm.industry}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Contact Person</p>
                    <p className="text-sm text-gray-900">{selectedIntakeForm.contact_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="text-sm text-gray-900">{selectedIntakeForm.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <p className="text-sm text-gray-900">{selectedIntakeForm.phone}</p>
                  </div>
                  {selectedIntakeForm.website && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Website</p>
                      <p className="text-sm text-gray-900">{selectedIntakeForm.website}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Project Details</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Project Type</p>
                    <p className="text-sm font-medium text-gray-900">{selectedIntakeForm.purpose}</p>
                  </div>
                  {selectedIntakeForm.target_audience && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Target Audience</p>
                      <p className="text-sm text-gray-900">{selectedIntakeForm.target_audience}</p>
                    </div>
                  )}
                  {selectedIntakeForm.desired_action && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Desired User Action</p>
                      <p className="text-sm text-gray-900">{selectedIntakeForm.desired_action}</p>
                    </div>
                  )}
                  {selectedIntakeForm.domain_name && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Domain Name</p>
                      <p className="text-sm text-gray-900">{selectedIntakeForm.domain_name}</p>
                    </div>
                  )}
                  {selectedIntakeForm.timeline && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Timeline</p>
                      <p className="text-sm text-gray-900">{selectedIntakeForm.timeline}</p>
                    </div>
                  )}
                  {selectedIntakeForm.budget_range && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Package Selected</p>
                      <p className="text-sm font-medium" style={{ color: '#D4AF37' }}>{selectedIntakeForm.budget_range}</p>
                    </div>
                  )}
                  {selectedIntakeForm.payment_method && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                      <p className="text-sm text-gray-900">{selectedIntakeForm.payment_method}</p>
                    </div>
                  )}
                  {selectedIntakeForm.additional_notes && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Additional Notes</p>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedIntakeForm.additional_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;